import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../services/api';
import { getSession } from '../utils/authUtils';

import logger from '../utils/logger';
const truthyStrings = ['true', '1', 'yes', 'y'];
const falseyStrings = ['false', '0', 'no', 'n'];

const ACCESS_FLAG_KEYS = [
  'hasGmbAccess',
  'hasGMBAccess',
  'has_gmb_access',
  'gmbAccess',
  'gmb_access',
  'gmb_access_enabled',
  'gbpAccess',
  'hasGBPAccess',
  'has_gbp_access',
  'hasGbpAccess',
];

const interpretAccessFlag = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (Number.isNaN(value)) return undefined;
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (truthyStrings.includes(normalized)) return true;
    if (falseyStrings.includes(normalized)) return false;
  }
  return undefined;
};

const extractAccessFlag = (source) => {
  if (!source || typeof source !== 'object') return undefined;
  for (const key of ACCESS_FLAG_KEYS) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const interpreted = interpretAccessFlag(source[key]);
      if (typeof interpreted === 'boolean') return interpreted;
    }
  }
  return undefined;
};

const inferAccessFromData = (data) => {
  if (!data) return undefined;

  const buckets = [
    data,
    data?.user,
    data?.data,
    data?.profile,
    data?.meta,
    data?.metadata,
  ];

  for (const bucket of buckets) {
    const interpreted = extractAccessFlag(bucket);
    if (typeof interpreted === 'boolean') {
      return interpreted;
    }
  }

  if (Array.isArray(data?.gmbAccounts) && data.gmbAccounts.length > 0) return true;
  if (Array.isArray(data?.accounts) && data.accounts.length > 0) return true;
  if (Array.isArray(data?.gmb?.accounts) && data.gmb.accounts.length > 0) return true;
  if (typeof data?.gmb_account_count === 'number' && data.gmb_account_count > 0) return true;

  return undefined;
};

const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [hasGmbAccess, setHasGmbAccess] = useState(true);
  useEffect(() => {
    const checkGmbAccess = async () => {
      try {
        const user = getSession();

        if (!user) {
          logger.log('No user session found in ProtectedRoute');
          setHasGmbAccess(false);
          return;
        }

        logger.log('User found in ProtectedRoute:', user);

        // Admin users (role_id 9) don't need GMB access check
        const adminRoles = [9];
        if (adminRoles.includes(user.role_id)) {
          logger.log('Admin user detected (role_id 9), skipping GMB access check');
          setHasGmbAccess(true);
          return;
        }

        // Allow all authenticated users to access dashboard
        // They can connect GMB accounts from within the dashboard
        setHasGmbAccess(true);

        // Optional: Still check GMB access for informational purposes
        const inferredAccess = inferAccessFromData(user);
        if (typeof inferredAccess === 'boolean') {
          logger.log('Inferred GMB access from user data:', inferredAccess);
        }

        try {
          const response = await api.get('/customer/auth/check-gmb-access');

          let resolvedAccess = inferAccessFromData(response);
          if (typeof resolvedAccess !== 'boolean') {
            resolvedAccess = inferAccessFromData(response?.data);
          }
          if (typeof resolvedAccess !== 'boolean') {
            resolvedAccess = Array.isArray(response?.gmbAccounts) && response.gmbAccounts.length > 0;
          }

          logger.log('GMB access check result:', resolvedAccess);
          // Don't block access even if no GMB accounts found
        } catch (apiError) {
          logger.warn('GMB access check failed, but allowing access:', apiError);
          // Still allow access even if API check fails
        }
      } catch (err) {
        logger.error('ProtectedRoute error:', err);
        setHasGmbAccess(true); // Still allow access on error
      } finally {
        setLoading(false);
      }
    };

    checkGmbAccess();
  }, []);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  const session = getSession();
  if (!session) {
    return <Navigate to="/" replace />;
  }

  if (hasGmbAccess === true) {
    logger.debug('[ProtectedRoute] Access granted. Rendering children.');
    return children;
  }

  if (hasGmbAccess === false) {
    logger.debug('[ProtectedRoute] Access denied. Redirecting to /account-not-found.');
    return <Navigate to="/account-not-found" replace />;
  }

  return children;
};

export default ProtectedRoute;
